import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Check, User } from "lucide-react";
import StatCard from "../../components/primitives/StatCard";
import { admin, ApiError } from "../../services/api";
import { relativeTime } from "../../utils/auditFormat";
import "../../styles/admin-shell.css";
import "./AdminPackDetailPage.css";

function AdminPackDetailPage() {
    const { id } = useParams();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const ctrl = new AbortController();
        setLoading(true);
        setError(null);
        (async () => {
            try {
                const data = await admin.getPack(id, { signal: ctrl.signal });
                if (!cancelled) setJob(data);
            } catch (err) {
                if (cancelled || err?.name === "AbortError") return;
                setError(err instanceof ApiError ? err.message : err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            ctrl.abort();
        };
    }, [id]);

    return (
        <div className="admin-page">
            <header className="admin-page__header">
                <div className="admin-page__title-block">
                    <Link to="/admin/users" className="admin-detail__back">
                        <ArrowLeft size={12} aria-hidden="true" />
                        Users
                    </Link>
                    <span className="admin-page__eyebrow uppercase-label">
                        Admin / Pack job
                    </span>
                    <h1 className="admin-page__title">
                        {loading && !job ? "Loading…" : job ? `Job #${job.id.slice(0, 8)}` : "Not found"}
                    </h1>
                    {job && (
                        <div className="pack-detail__sub">
                            <span className="mono">{job.endpoint}</span>
                            <span className="pack-detail__sep">·</span>
                            <span className="mono">{relativeTime(job.created_at)}</span>
                            {job.user_id && (
                                <>
                                    <span className="pack-detail__sep">·</span>
                                    <Link
                                        to={`/admin/users/${job.user_id}`}
                                        className="pack-detail__user-link"
                                    >
                                        <User size={11} aria-hidden="true" />
                                        view user
                                    </Link>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </header>

            <div className="admin-page__body">
                {error && (
                    <div className="admin-error" role="alert">
                        <AlertTriangle size={14} aria-hidden="true" />
                        <span>{error}</span>
                    </div>
                )}

                {job && (
                    <>
                        <section className="admin-section">
                            <header className="admin-section__head">
                                <h2 className="admin-section__title">Outcome</h2>
                            </header>
                            <div className="admin-stats">
                                <StatCard
                                    label="Status"
                                    value={
                                        job.error_code ? (
                                            <span style={{ color: "var(--color-danger)" }}>
                                                {job.error_code}
                                            </span>
                                        ) : (
                                            <span style={{ color: "var(--color-success)" }}>
                                                <Check size={20} aria-hidden="true" />
                                            </span>
                                        )
                                    }
                                />
                                <StatCard
                                    label="Fitted"
                                    value={
                                        job.fitted_count != null ? job.fitted_count : "—"
                                    }
                                    tone="accent"
                                />
                                <StatCard
                                    label="Duration"
                                    value={job.duration_ms != null ? job.duration_ms : "—"}
                                    unit={job.duration_ms != null ? "ms" : undefined}
                                />
                                <StatCard
                                    label="Endpoint"
                                    value={
                                        <span className="mono" style={{ fontSize: "var(--fs-base)" }}>
                                            {job.endpoint}
                                        </span>
                                    }
                                />
                            </div>
                        </section>

                        <div className="pack-detail__cols">
                            <section className="admin-section">
                                <header className="admin-section__head">
                                    <h2 className="admin-section__title">Request</h2>
                                    <span className="admin-section__hint">
                                        what the user sent
                                    </span>
                                </header>
                                <pre className="pack-detail__json">
                                    {JSON.stringify(job.request, null, 2)}
                                </pre>
                            </section>
                            <section className="admin-section">
                                <header className="admin-section__head">
                                    <h2 className="admin-section__title">Response summary</h2>
                                    <span className="admin-section__hint">
                                        full placements omitted
                                    </span>
                                </header>
                                {job.response ? (
                                    <pre className="pack-detail__json">
                                        {JSON.stringify(job.response, null, 2)}
                                    </pre>
                                ) : (
                                    <div className="admin-empty mono">
                                        No response captured.
                                    </div>
                                )}
                            </section>
                        </div>

                        {job.request_id && (
                            <section className="admin-section">
                                <div className="pack-detail__rid">
                                    <span className="uppercase-label">Request id</span>
                                    <span className="mono">{job.request_id}</span>
                                </div>
                            </section>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default AdminPackDetailPage;
